/**
 * The tenant SLUG the current session belongs to.
 *
 * On a per-company address (`acme.hr.linkco.jo`) the slug is in the hostname
 * and this store is never consulted. On the global entry host
 * (`app.[staging.]hr.linkco.jo`) the address names no company at all, so the
 * only honest source is the session itself: every 200 from `/auth/login`
 * — success, `otp_required` and `totp_required` alike — carries `tenantSlug`,
 * and `performLogin` records it here.
 *
 * Distinct from {@link sessionTenantMarker}, which holds the tenant **id** and
 * exists to REFUSE a session established on another host. This holds the
 * **slug** and exists to SUPPLY one where the host has none. They answer
 * different questions and are deliberately not merged.
 *
 * Audience-scoped for the same reason the marker is: `admin` and `mobile`
 * share an origin, and one signing in as another company must not relabel the
 * other's screens.
 *
 * Same storage posture as {@link userEmailHintStore} — a slug is not a secret
 * (it is in the URL on every per-company address) and is cleared on logout.
 */
import { getAudience } from "../refresh/authNetworkConfig.js";

const STORAGE_KEY_PREFIX = "linkhr:auth:tenantSlug:";

/** Backend slug grammar (`^[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])?$`). */
const SAFE_SLUG = /^[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])?$/;

const memoryFallback = new Map<string, string>();

function storageKey(aud?: string): string {
  return STORAGE_KEY_PREFIX + (aud ?? getAudience());
}

function tryLocalStorage<T>(op: (ls: Storage) => T, fallback: T): T {
  try {
    if (typeof window === "undefined") return fallback;
    const ls = window.localStorage;
    if (!ls) return fallback;
    return op(ls);
  } catch {
    return fallback;
  }
}

export const sessionTenantSlugStore = {
  /** `aud` defaults to the configured audience; the global login passes the
   *  audience it actually signed in with, which is decided per attempt. */
  set(slug: string | null | undefined, aud?: string): void {
    if (typeof slug !== "string" || !SAFE_SLUG.test(slug)) return;
    const key = storageKey(aud);
    memoryFallback.set(key, slug);
    tryLocalStorage((ls) => ls.setItem(key, slug), undefined);
  },

  get(aud?: string): string | null {
    const key = storageKey(aud);
    const stored = tryLocalStorage((ls) => ls.getItem(key), null);
    const value = stored ?? memoryFallback.get(key) ?? null;
    // A hand-edited / legacy value must never become a tenant header.
    return value !== null && SAFE_SLUG.test(value) ? value : null;
  },

  clear(aud?: string): void {
    const key = storageKey(aud);
    memoryFallback.delete(key);
    tryLocalStorage((ls) => ls.removeItem(key), undefined);
  },
};
